import React, { useState } from 'react';
import Modal from 'src/shared/components/modal/Modal';
import Checkbox from 'src/shared/components/checkbox/Checkbox';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import type { CreateApiTokenResponse } from '../../state/api/engineApiTokensEngineSlice';

type RevealTokenModalProps = {
    payload: CreateApiTokenResponse;
    engineApiUrl: string;
    onClose: () => void;
};

const ACKNOWLEDGEMENT_LABEL = 'I have saved this token securely — it cannot be retrieved again.';

const buildCurlExample = (rawToken: string, engineApiUrl: string): string =>
    `curl -H "Authorization: Bearer ${rawToken}" ${engineApiUrl}/orgs/<bcId>/sites`;

const RevealTokenModal = ({ payload, engineApiUrl, onClose }: RevealTokenModalProps) => {
    const [acknowledged, setAcknowledged] = useState(false);

    const handleCopy = () => {
        void navigator.clipboard.writeText(payload.token);
    };

    return (
        <Modal dataTestId='reveal-token-modal'>
            <div className='flex flex-col gap-5 p-2 min-w-[32rem] max-w-2xl'>
                <header className='flex flex-col gap-1'>
                    <h2 className='text-xl font-semibold text-primary'>Token created — save it now</h2>
                    <p className='text-sm text-grey-70'>This is the only time you will see the raw value. The engine does not store it.</p>
                </header>

                <section className='flex flex-col gap-2'>
                    <span className='text-xs uppercase tracking-wide text-grey-60'>Name</span>
                    <span className='text-sm text-primary'>{payload.name}</span>
                </section>

                <section className='flex flex-col gap-2'>
                    <span className='text-xs uppercase tracking-wide text-grey-60'>Token</span>
                    <div className='flex items-center gap-2'>
                        <code className='flex-1 text-xs bg-grey-10 px-3 py-2 rounded break-all' data-testid='reveal-token-value'>
                            {payload.token}
                        </code>
                        <SecondaryButton onClick={handleCopy}>Copy</SecondaryButton>
                    </div>
                </section>

                <section className='flex flex-col gap-2'>
                    <span className='text-xs uppercase tracking-wide text-grey-60'>Scopes</span>
                    <div className='flex flex-wrap gap-1'>
                        {payload.allowed_scopes.map(scope => (
                            <span key={scope} className='text-xs bg-grey-10 px-2 py-0.5 rounded'>
                                {scope}
                            </span>
                        ))}
                    </div>
                </section>

                <section className='flex flex-col gap-2'>
                    <span className='text-xs uppercase tracking-wide text-grey-60'>Usage</span>
                    <pre className='text-xs bg-grey-10 px-3 py-2 rounded overflow-x-auto' data-testid='reveal-token-curl'>
                        {buildCurlExample(payload.token, engineApiUrl)}
                    </pre>
                </section>

                <label className='flex items-start gap-3 cursor-pointer pt-2 border-t border-grey-10'>
                    <Checkbox checked={acknowledged} onChange={setAcknowledged} ariaLabel='I have saved this token securely' />
                    <span className='text-sm text-primary'>{ACKNOWLEDGEMENT_LABEL}</span>
                </label>

                <div className='flex justify-end'>
                    <PrimaryButton onClick={onClose} disabled={!acknowledged}>
                        Done
                    </PrimaryButton>
                </div>
            </div>
        </Modal>
    );
};

export default RevealTokenModal;
